import React from "react";
import { motion } from "framer-motion";
import { Users } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { tooltipStyle } from "./utils";

interface TeamUtilizationChartProps {
  data: { name: string; billableMinutes: number; totalMinutes: number }[];
}

const TeamUtilizationChart: React.FC<TeamUtilizationChartProps> = ({ data }) => {
  const chartData = data.map((d) => ({
    name: d.name.length > 18 ? d.name.substring(0, 18) + "..." : d.name,
    fullName: d.name,
    billable: d.billableMinutes / 60,
    nonBillable: Math.max(0, d.totalMinutes - d.billableMinutes) / 60,
  }));

  const isEmpty = !data.length || data.every((d) => d.totalMinutes === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg" style={{ backgroundColor: "#1987541A" }}>
          <Users className="h-4 w-4" style={{ color: "#198754" }} />
        </div>
        <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">
          Team Utilization
        </h3>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
            <Users className="h-6 w-6 text-slate-300" />
          </div>
          <p className="text-sm font-medium text-slate-500">No time entries</p>
          <p className="text-xs text-slate-400">Start tracking time to see utilization</p>
        </div>
      ) : (
        <ResponsiveContainer
          width="100%"
          height={Math.max(300, chartData.length * 50)}
        >
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ left: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickFormatter={(v) => `${v.toFixed(1)}h`}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: "#64748b" }}
              width={120}
            />
            <Tooltip
              formatter={(value: number | string | undefined, name: string | undefined) => [
                `${Number(value ?? 0).toFixed(1)} hours`,
                name === "billable" ? "Billable" : "Non-Billable",
              ]}
              contentStyle={tooltipStyle}
            />
            <Legend
              formatter={(value: string) => (
                <span className="text-xs text-slate-600 capitalize">
                  {value === "billable" ? "Billable" : "Non-Billable"}
                </span>
              )}
            />
            <Bar
              dataKey="billable"
              stackId="hours"
              fill="#198754"
              radius={[0, 0, 0, 0]}
              barSize={20}
            />
            <Bar
              dataKey="nonBillable"
              stackId="hours"
              fill="#e2e8f0"
              radius={[0, 4, 4, 0]}
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  );
};

export default TeamUtilizationChart;
